import { type ReactNode, useLayoutEffect } from "react";
import { atom, useSetAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";

export type FabMenuItem = {
  id: string;
  label: string;
  description?: string;
  icon: ReactNode;
  onClick: () => void;
  endAdornment?: ReactNode;
};

export type FabMenuSection = {
  id: string;
  header?: string;
  items: FabMenuItem[];
};

export type FabMenuConfig = {
  sections: FabMenuSection[];
  hidden?: boolean;
};

export const DEFAULT_FAB_MENU_CONFIG: FabMenuConfig = {
  sections: [],
  hidden: false,
};

export const fabMenuAtom = atom<FabMenuConfig>(DEFAULT_FAB_MENU_CONFIG);

// User-driven hide toggle (Topbar "Help button"). Distinct from FabMenuConfig.hidden,
// which is tour-driven and overwritten by TourFab each render.
// getOnInit reads the persisted value synchronously on first render, avoiding a
// one-frame FAB flash when the stored state is hidden=true (default getOnInit:false
// would return `false` first, then sync to `true` an effect later).
export const fabHiddenAtom = atomWithStorage<boolean>(
  "definity:fab:hidden",
  false,
  undefined,
  { getOnInit: true }
);

export const useFabMenuConfig = (config: FabMenuConfig) => {
  const setConfig = useSetAtom(fabMenuAtom);

  // biome-ignore lint/correctness/useExhaustiveDependencies: setConfig stable (Jotai setter)
  useLayoutEffect(() => {
    setConfig(config);

    return () => setConfig(DEFAULT_FAB_MENU_CONFIG);
  }, [config]);
};
